import React, {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';
import {CheckCircleOutlined} from '@ant-design/icons';
import InputMask from 'react-input-mask';
import {useTranslation} from 'react-i18next';
import classes from '../courses.module.css';
import IntlMessage from '../../../components/IntlMessage/IntlMessage';
import SectionTitle from './SectionTitle';
import ApplicationService from '../../../services/ApplicationService';
import useLocalizedText from '../hooks/useLocalizedText';

/** Категория, заведённая в InitService для этой витрины. */
export const CORPORATE_CATEGORY_NAME_ENG = 'Corporate courses';

const OTHER = '__other__';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Форма заявки на корпоративное обучение.
 *
 * Один компонент на обе страницы. Переключатель «Для себя / Для компании» меняет
 * не оформление, а состав данных: у корпоративного заказчика менеджеру нужны
 * компания, должность и количество обучаемых — без них он всё равно перезвонит
 * и спросит это первым делом.
 */
const CourseApplyForm = ({courses, defaultCourseSlug, prefillComment, anchorRef, tone = 'light'}) => {
    const {t} = useTranslation();
    const pick = useLocalizedText();
    const location = useLocation();
    const categories = useSelector((state) => state.categoriesList.categories);

    const [forCompany, setForCompany] = useState(true);
    const [values, setValues] = useState({
        name: '',
        telNumber: '',
        email: '',
        position: '',
        companyName: '',
        employeesCount: '',
        courseSlug: defaultCourseSlug || '',
        comment: '',
        website: '', // honeypot
    });
    const [consent, setConsent] = useState(false);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState('idle'); // idle | sending | done
    const [serverError, setServerError] = useState(null);

    useEffect(() => {
        if (prefillComment) {
            setValues((v) => (v.comment ? v : {...v, comment: prefillComment}));
        }
    }, [prefillComment]);

    useEffect(() => {
        if (defaultCourseSlug) setValues((v) => ({...v, courseSlug: defaultCourseSlug}));
    }, [defaultCourseSlug]);

    const category = useMemo(
        () => categories.find((c) => c?.nameEng === CORPORATE_CATEGORY_NAME_ENG),
        [categories],
    );

    const set = (key) => (e) => {
        const value = e?.target ? e.target.value : e;
        setValues((v) => ({...v, [key]: value}));
        setErrors((prev) => (prev[key] ? {...prev, [key]: undefined} : prev));
    };

    const validate = () => {
        const next = {};
        if (values.name.trim().length < 2) next.name = t('coursesPage.form.errorName');
        const phone = values.telNumber.replace(/[^\d+]/g, '');
        if (values.telNumber.includes('_') || phone.length < 11) next.telNumber = t('coursesPage.form.errorPhone');
        if (!EMAIL_RE.test(values.email.trim())) next.email = t('coursesPage.form.errorEmail');
        if (forCompany && values.companyName.trim().length < 2) {
            next.companyName = t('coursesPage.form.errorCompany');
        }
        if (!consent) next.consent = t('coursesPage.form.errorConsent');
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    /**
     * Курс кладём структурированным префиксом в comment: своей колонки под него в БД
     * пока нет, а заводить её сейчас — значит создать третий несогласованный справочник
     * рядом с Category и Course. Менеджер видит префикс в колонке «Примечания»,
     * а на этапе с БД он разбирается одним скриптом.
     */
    const buildComment = () => {
        const parts = [];
        if (values.courseSlug && values.courseSlug !== OTHER) {
            const course = courses.find((c) => c.slug === values.courseSlug);
            if (course) parts.push(`Курс: ${pick(course.title)} [${course.slug}]`);
        } else if (values.courseSlug === OTHER) {
            parts.push('Курс: нужна своя программа');
        }
        parts.push(`Источник: ${location.pathname}${location.search}`);
        if (values.comment.trim()) parts.push('---', values.comment.trim());
        return parts.join('\n');
    };

    const submit = async (e) => {
        e.preventDefault();
        setServerError(null);
        if (!validate()) return;
        if (!category) {
            setServerError(t('coursesPage.form.errorNoCategory'));
            return;
        }

        setStatus('sending');
        try {
            const phone = values.telNumber.endsWith('_')
                ? values.telNumber.replace(/_+$/, '')
                : values.telNumber;
            await ApplicationService.saveApplication({
                name: values.name.trim(),
                email: values.email.trim(),
                telNumber: phone,
                categoryId: category.id,
                applicantType: forCompany ? 'COMPANY' : 'INDIVIDUAL',
                companyName: forCompany ? values.companyName.trim() : undefined,
                position: forCompany ? values.position.trim() : undefined,
                employeesCount: forCompany && values.employeesCount
                    ? Number(values.employeesCount)
                    : undefined,
                comment: buildComment(),
                website: values.website,
            });
            setStatus('done');
        } catch (reason) {
            const data = reason?.response?.data;
            const code = reason?.response?.status;
            if (code === 429) setServerError(t('coursesPage.form.errorTooMany'));
            else if (data === 'application already exists') setServerError(t('coursesPage.form.errorDuplicate'));
            else if (data === 'invalid phone') setServerError(t('coursesPage.form.errorPhone'));
            else if (data === 'company name required') setServerError(t('coursesPage.form.errorCompany'));
            else setServerError(t('coursesPage.form.errorGeneric'));
            setStatus('idle');
        }
    };

    const field = (key, labelKey, {required, placeholder, type = 'text', inputMode} = {}) => (
        <label className={classes.field}>
            <span className={classes.fieldLabel}>
                <IntlMessage id={labelKey}/>
                {required && <span className={classes.fieldRequired}> *</span>}
            </span>
            <input
                className={`${classes.input} ${errors[key] ? classes.inputError : ''}`}
                type={type}
                inputMode={inputMode}
                value={values[key]}
                placeholder={placeholder ? t(placeholder) : undefined}
                onChange={set(key)}
            />
            {errors[key] && <span className={classes.fieldError}>{errors[key]}</span>}
        </label>
    );

    return (
        <section
            className={tone === 'alt' ? classes.sectionAlt : classes.section}
            ref={anchorRef}
            style={{scrollMarginTop: 120}}
        >
            <div className={classes.formGrid}>
                <div className={classes.formLeft}>
                    <SectionTitle id="coursesPage.form.title"/>
                    <p className={classes.formLead}><IntlMessage id="coursesPage.form.leadText"/></p>
                    <ul className={classes.formBullets}>
                        {['b1', 'b2', 'b3'].map((k) => (
                            <li className={classes.formBullet} key={k}>
                                <CheckCircleOutlined/>
                                <IntlMessage id={`coursesPage.form.${k}`}/>
                            </li>
                        ))}
                    </ul>
                    <div className={classes.formContacts}>
                        <a className={classes.formPhone} href="tel:+77713256171">+7 771 325 6171</a>
                        <span className={classes.formContactNote}>
                            <IntlMessage id="coursesPage.form.contactNote"/>
                        </span>
                    </div>
                </div>

                {status === 'done' ? (
                    <div className={classes.formCard}>
                        <div className={classes.formSuccess}>
                            <CheckCircleOutlined className={classes.formSuccessIcon}/>
                            <h3 className={classes.formSuccessTitle}>
                                <IntlMessage id="coursesPage.form.successTitle"/>
                            </h3>
                            <p className={classes.formSuccessText}>
                                <IntlMessage id="coursesPage.form.successText"/>
                            </p>
                        </div>
                    </div>
                ) : (
                    <form className={classes.formCard} onSubmit={submit} noValidate>
                        <div className={classes.toggle} role="tablist">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={!forCompany}
                                className={`${classes.toggleOpt} ${!forCompany ? classes.toggleOptActive : ''}`}
                                onClick={() => setForCompany(false)}
                            >
                                <IntlMessage id="coursesPage.form.forSelf"/>
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={forCompany}
                                className={`${classes.toggleOpt} ${forCompany ? classes.toggleOptActive : ''}`}
                                onClick={() => setForCompany(true)}
                            >
                                <IntlMessage id="coursesPage.form.forCompany"/>
                            </button>
                        </div>

                        <div className={classes.formRow}>
                            {field('name', 'coursesPage.form.name', {
                                required: true,
                                placeholder: 'coursesPage.form.namePlaceholder',
                            })}
                            <label className={classes.field}>
                                <span className={classes.fieldLabel}>
                                    <IntlMessage id="coursesPage.form.phone"/>
                                    <span className={classes.fieldRequired}> *</span>
                                </span>
                                <InputMask
                                    mask="+7 (999) 999-99-99"
                                    value={values.telNumber}
                                    onChange={set('telNumber')}
                                >
                                    {(inputProps) => (
                                        <input
                                            {...inputProps}
                                            type="tel"
                                            inputMode="tel"
                                            className={`${classes.input} ${errors.telNumber ? classes.inputError : ''}`}
                                            placeholder="+7 (___) ___-__-__"
                                        />
                                    )}
                                </InputMask>
                                {errors.telNumber && <span className={classes.fieldError}>{errors.telNumber}</span>}
                            </label>
                        </div>

                        <div className={classes.formRow}>
                            {field('email', 'coursesPage.form.email', {
                                required: true,
                                type: 'email',
                                inputMode: 'email',
                                placeholder: 'coursesPage.form.emailPlaceholder',
                            })}
                            {field('position', 'coursesPage.form.position', {
                                placeholder: 'coursesPage.form.positionPlaceholder',
                            })}
                        </div>

                        <div className={`${classes.b2bBlock} ${forCompany ? classes.b2bBlockOpen : ''}`}>
                            <div className={classes.b2bInner}>
                                <div className={classes.formRow}>
                                    {field('companyName', 'coursesPage.form.company', {
                                        required: true,
                                        placeholder: 'coursesPage.form.companyPlaceholder',
                                    })}
                                    {field('employeesCount', 'coursesPage.form.employeesCount', {
                                        type: 'number',
                                        inputMode: 'numeric',
                                        placeholder: 'coursesPage.form.employeesPlaceholder',
                                    })}
                                </div>
                            </div>
                        </div>

                        <label className={classes.field}>
                            <span className={classes.fieldLabel}>
                                <IntlMessage id="coursesPage.form.courseSelect"/>
                            </span>
                            <select className={classes.select} value={values.courseSlug} onChange={set('courseSlug')}>
                                <option value="">{t('coursesPage.form.coursePlaceholder')}</option>
                                {courses.map((c) => (
                                    <option value={c.slug} key={c.slug}>{pick(c.title)}</option>
                                ))}
                                <option value={OTHER}>{t('coursesPage.form.courseOther')}</option>
                            </select>
                        </label>

                        <label className={classes.field}>
                            <span className={classes.fieldLabel}>
                                <IntlMessage id="coursesPage.form.comment"/>
                            </span>
                            <textarea
                                className={classes.textarea}
                                value={values.comment}
                                placeholder={t('coursesPage.form.commentPlaceholder')}
                                onChange={set('comment')}
                            />
                        </label>

                        {/* honeypot: скрыт от человека, но не от простого бота */}
                        <input
                            className={classes.honeypot}
                            type="text"
                            tabIndex={-1}
                            autoComplete="off"
                            aria-hidden="true"
                            value={values.website}
                            onChange={set('website')}
                        />

                        <label className={classes.consent}>
                            <input
                                type="checkbox"
                                checked={consent}
                                onChange={(e) => {
                                    setConsent(e.target.checked);
                                    setErrors((prev) => ({...prev, consent: undefined}));
                                }}
                            />
                            <span>
                                <IntlMessage id="coursesPage.form.consent"/>
                                {errors.consent && <span className={classes.fieldError}> {errors.consent}</span>}
                            </span>
                        </label>

                        {serverError && <div className={classes.fieldError}>{serverError}</div>}

                        <button
                            type="submit"
                            className={`${classes.btnAccent} ${classes.btnBlock}`}
                            disabled={status === 'sending'}
                        >
                            <IntlMessage id={status === 'sending' ? 'coursesPage.form.sending' : 'coursesPage.form.submit'}/>
                        </button>

                        <p className={classes.formNote}><IntlMessage id="coursesPage.enroll.trust"/></p>
                    </form>
                )}
            </div>
        </section>
    );
};

export default CourseApplyForm;
